import {PlusCircleOutlined} from '@ant-design/icons';
import {Button, Popconfirm} from 'antd';
import cn from 'classnames';
import {omitBy} from 'lodash-es';
import React, {
    forwardRef,
    createRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import {v4 as uid} from 'uuid';

import {CustomRadio} from 'components/@common/widgets/custom-radio';
import {IconsMap} from 'components/dynamic-icon';
import {EFilterValueType} from 'features/predicate-expression-form-list';
import {ITableAttributes} from 'shared/types';
import {ILookupEntry} from 'shared/types/lookups';
import {isEmptyObject} from 'shared/utils';
import {TAppState} from 'store/config/types';

import {TableColumnFilterExpressionOperand, ITableColumnFilterExpressionOperandRef} from './table-column-filter-operand';
import {TTableColumnFilterExpessionGroupIdentificator, TTableColumnFilterExpression, TTableColumnFilterOperand} from './table-column-filter-types';

interface ITableColumnFilterExpressionGroupProps {
    initialExpression: TTableColumnFilterExpression;
    className?: cn.Argument;
    isChild?: boolean;
    remove?: () => void;
    isInEditMode: boolean;
    depth: number;
    itemIndex: string;
    columnName: string;
    selectColumnFilterEntriesByFieldType: (state: TAppState, params: {
      fieldType: EFilterValueType;
    }) => ILookupEntry[] | undefined;
    tableAttributes?: ITableAttributes[];
}

export interface ITableColumnFilterExpressionGroupRef {
    getValues: () => any;
    setExpression: (exp: TTableColumnFilterExpression) => void;
}

export const TableColumnFilterExpressionGroup = forwardRef<
ITableColumnFilterExpressionGroupRef, ITableColumnFilterExpressionGroupProps>((
    {
        initialExpression,
        className,
        isChild,
        remove,
        isInEditMode,
        depth,
        itemIndex,
        columnName,
        selectColumnFilterEntriesByFieldType,
        tableAttributes,
    }: ITableColumnFilterExpressionGroupProps,
    ref,
) => {
    const fillOperandsWithUid = (operands: TTableColumnFilterOperand[]) => operands.map(oper => {
        if (!Array.isArray(oper)) {
            return {
                uid: oper.uid ?? uid(),
                ...oper,
            };
        }

        return oper;
    });

    const [identificator, setIdentificator] = useState({
        uid: uid(),
        ...initialExpression[0],
    } as TTableColumnFilterExpessionGroupIdentificator);

    const [operands, setOperands] = useState<TTableColumnFilterOperand[]>(
        fillOperandsWithUid(initialExpression.slice(1) as TTableColumnFilterOperand[]),
    );

    useEffect(() => {
        if (!depth) {
            setOperands(fillOperandsWithUid(initialExpression.slice(1) as TTableColumnFilterOperand[]));
            setIdentificator(p => ({...p, ...initialExpression[0]}));
        }
    }, [initialExpression]);

    const operandRefs = useRef<{current: ITableColumnFilterExpressionOperandRef}[]>([]);
    operandRefs.current = operands.map((element, i) => operandRefs.current[i] ?? createRef());

    const getValues = async () => {
        const list = operandRefs.current ?? [];

        const valuesFromOperand = await Promise.all(list.map(item => item.current.getValues()));

        const values = valuesFromOperand.filter(vals => {
            if (typeof vals === 'object' && isEmptyObject(omitBy(vals, v => !v))) return false;
            return !(Array.isArray(vals) && vals.length === 1);
        });

        return [
            {connectBy: identificator.connectBy},
            ...values,
        ];
    };

    const setExpression = (exp: TTableColumnFilterExpression) => {
        setIdentificator({
            uid: exp[0]?.uid ?? uid(),
            ...exp[0],
        });
        setOperands(fillOperandsWithUid(exp.slice(1) as TTableColumnFilterOperand[]));
    };

    useImperativeHandle(ref, () => ({getValues, setExpression}));

    const generateUidKeyOperand = (oper: TTableColumnFilterOperand) => (
        !Array.isArray(oper) ? oper.uid : (oper?.[0].uid ?? uid())
    );

    return (
        <div
            className={cn('table-column-filter-expression-group', {
                'table-column-filter-expression-group_main': !isChild,
            }, className)}
        >
            <div
                className={cn('table-column-filter-expression-group__upbar')}
            >
                <div className="d-flex gap-1-5 align-items-center">
                    <CustomRadio
                        disabled={!isInEditMode}
                        value={identificator.connectBy}
                        size="small"
                        onChange={e => {
                            setIdentificator(p => ({
                                ...p,
                                connectBy: e.target.value,
                            }));
                        }}
                        items={[{
                            label: '«ИЛИ»',
                            value: 'OR',
                        }, {
                            label: '«И»',
                            value: 'AND',
                        }]}
                    />
                </div>

                <div className="d-flex gap-1 align-items-center">
                    <Button
                        disabled={!isInEditMode}
                        type="default"
                        size="small"
                        onClick={async () => {
                            setOperands(p => [...p, {
                                columnName: undefined,
                                operator: undefined,
                                value: '',
                                uid: uid(),
                            }]);
                        }}
                    >
                        <PlusCircleOutlined />
                        Правило
                    </Button>
                    <Button
                        disabled={!isInEditMode}
                        type="default"
                        size="small"
                        onClick={() => {
                            setOperands(p => [...p, [{
                                connectBy: 'OR',
                                uid: uid(),
                                enabled: true,
                            }]]);
                        }}
                    >
                        <PlusCircleOutlined />
                        Группа
                    </Button>
                    {isChild && (
                        <Popconfirm
                            title={(
                                <span>Вы действительно хотите удалить группу?</span>
                            )}
                            placement="bottomLeft"
                            okText="Удалить"
                            disabled={!operands.length || !isChild}
                            onConfirm={() => {
                                remove?.();
                            }}
                        >
                            <Button
                                size="small"
                                disabled={!isChild || !isInEditMode}
                                type="default"
                                className="btn-only-icon"
                                onClick={() => {
                                    if (!operands.length) remove?.();
                                }}
                            >
                                <IconsMap.TrashXOutlined />
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            </div>
            <div
                className={cn(
                    'table-column-filter-expression-group__operands',
                )}
            >
                <div className={cn({
                    'table-column-filter-expression-group__operands__border': operands.length,
                })}
                />
                <div className={cn('table-column-filter-expression-group__operands__body')}>
                    {operands.map((oper, index) => (
                        <TableColumnFilterExpressionOperand
                            isInEditMode={isInEditMode}
                            ref={operandRefs.current[index]}
                            operand={oper}
                            key={generateUidKeyOperand(oper)}
                            remove={() => {
                                setOperands(() => (
                                    operands.filter((v, i) => i !== index)
                                ));
                            }}
                            depth={depth + 1}
                            itemIndex={!depth && itemIndex === '1' ? `${index + 1}` : `${itemIndex}.${index + 1}`}
                            columnName={columnName}
                            selectColumnFilterEntriesByFieldType={selectColumnFilterEntriesByFieldType}
                            tableAttributes={tableAttributes}
                        />
                    ))}
                    {!operands.length && (
                        <div className={cn('table-column-filter-expression-group__operands__body__no-rules')}>
                            Необходимо добавить правило или группу
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
